import { useEstabelecimento } from "@/contexts/cadastro/estabelecimento";
import { ModalHistoricoAnalise } from "@/contexts/laboratorio/analiseLaboratorial/components/ModalHistoricoAnalise/ModalHistoricoAnalise";
import { RequisicaoAnaliseFormValues } from "@/contexts/laboratorio/analiseLaboratorial/model/RequisicaoAnaliseFormValues";
import { useEnsaiosAnalise } from "@/contexts/laboratorio/ensaioAnalise";
import { useLaboratorioAnalise } from "@/contexts/laboratorio/laboratorioAnalise";
import { useMarcaProduto } from "@/contexts/laboratorio/marcaProduto";
import { useProdutoAutorizado } from "@/contexts/laboratorio/produtoAutorizado";
import { useProgramacaoColetaAnalise } from "@/contexts/laboratorio/programacaoColeta";
import { useNetworkStatus } from "@/shared/hooks/useNetworkStatus";
import { temperaturaMask } from "@/shared/utils/temperaturaMask";
import { OrderedListOutlined } from "@ant-design/icons";
import {
  App,
  Button,
  Checkbox,
  CheckboxChangeEvent,
  Col,
  DatePicker,
  Divider,
  Form,
  FormInstance,
  Grid,
  Input,
  Radio,
  RadioChangeEvent,
  Row,
  Select,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";

export function FormDadosGeraisRequisicao({
  form,
  isEdit = false,
}: {
  form: FormInstance<RequisicaoAnaliseFormValues>;
  isEdit?: boolean;
}) {
  const screens = Grid.useBreakpoint();
  const { isOnline } = useNetworkStatus();
  const { message } = App.useApp();
  const { estabelecimentoOptions } = useEstabelecimento();
  const { laboratoriosOptions } = useLaboratorioAnalise();
  const {
    getProgramacoesByEstabelecimento,
    programacoesOptions,
    getProgramacaoById,
  } = useProgramacaoColetaAnalise();
  const { getMarcasProdutosByEstabelecimento, marcasOptions } =
    useMarcaProduto();
  const {
    produtosOptions,
    listaProdutosAutorizados,
    getProdutosByEstabelecimento,
  } = useProdutoAutorizado();
  const { ensaioOptions, getEnsaiosByNatureza } = useEnsaiosAnalise();
  const aguaChecked = Form.useWatch("agua", form);
  const naturezaAnalise = Form.useWatch("naturezaAnalise", form);
  const estabelecimentoId = Form.useWatch(["estabelecimento", "id"], form);
  const produtoId = Form.useWatch(["produto", "id"], form);
  const programacaoId = Form.useWatch(["programacao", "id"], form);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isProgramacaoOutlier, setIsProgramacaoOutlier] = useState(false);
  const [isProgramacaoDiffProduto, setIsProgramacaoDiffProduto] =
    useState(false);

  const handleOpenModalHistorico = () => {
    setIsOpenModal(true);
  };

  const handleCloseModalHistorico = () => {
    setIsOpenModal(false);
  };

  const handleChangeEstabelecimento = (value: number) => {
    getProgramacoesByEstabelecimento(value);
    getMarcasProdutosByEstabelecimento(value);
    getProdutosByEstabelecimento(value);
    form.setFieldValue(["programacao", "id"], undefined);
    form.setFieldValue(["produto", "id"], undefined);
    form.setFieldValue("ensaios", undefined);
    form.setFieldValue(["marca", "id"], undefined);
  };

  const handleChangeAgua = (e: CheckboxChangeEvent) => {
    const { checked } = e.target;

    const programacaoSelected = getProgramacaoById(programacaoId);

    if (programacaoSelected) {
      setIsProgramacaoDiffProduto(programacaoSelected?.agua !== checked);
    }

    if (checked) {
      form.setFieldValue(["produto", "id"], undefined);
      form.setFieldValue(["marca", "id"], undefined);
      form.setFieldValue("ensaios", undefined);
    }
  };

  const handleChangeProgramacao = (value: number) => {
    const programacao = getProgramacaoById(value);

    if (!programacao) {
      setIsProgramacaoOutlier(false);
      setIsProgramacaoDiffProduto(false);
      return;
    }

    const hoje = dayjs();
    const dataProgramada = dayjs(programacao.dataProgramada);
    const dataFinalProgramada = dayjs(programacao.dataFinalProgramada);

    setIsProgramacaoOutlier(
      hoje.isBefore(dataProgramada, "day") ||
        hoje.isAfter(dataFinalProgramada, "day")
    );

    if (programacao.dataFinalProgramada === null) {
      message.warning(
        "A programação selecionada não possui data final definida."
      );
    }

    if (programacao.agua) {
      form.setFieldValue("agua", true);
      form.setFieldValue(["produto", "id"], undefined);
      return;
    }

    form.setFieldValue(["produto", "id"], programacao.produto?.id);
    form.setFieldValue("agua", false);
  };

  const handleChangeProduto = (value: number) => {
    const produto = listaProdutosAutorizados.find((p) => p.id === value);

    if (!produto) {
      setIsProgramacaoDiffProduto(false);
    }

    if (programacaoId) {
      const programacao = getProgramacaoById(programacaoId);

      setIsProgramacaoDiffProduto(programacao?.produto?.id !== value);
    }

    if (produto && naturezaAnalise) {
      const ensaiosPreSelecionados = ensaioOptions.filter((ensaio) => {
        return produto.produto?.ensaios.some(
          (e) => e.ensaio.id === ensaio.value
        );
      });
      form.setFieldValue(
        "ensaios",
        ensaiosPreSelecionados.map((e) => e.value)
      );
      return;
    }

    form.setFieldValue("ensaios", []);
  };

  const handleChangeNaturezaAnalise = (e: RadioChangeEvent) => {
    const natureza = e.target.value;
    getEnsaiosByNatureza(natureza);
  };

  const getDisabledDate = (current: dayjs.Dayjs | null): boolean => {
    if (!current) return false;

    const today = dayjs();
    const thirtyDaysAgo = today.subtract(30, "day");

    return (
      current.isAfter(today, "day") || current.isBefore(thirtyDaysAgo, "day")
    );
  };

  const getDisabledTime = (current: dayjs.Dayjs | null) => {
    if (!current) return {};

    const today = dayjs();
    const isToday = current.isSame(today, "day");

    if (isToday) {
      const currentHour = today.hour();
      const currentMinute = today.minute();

      return {
        disabledHours: () => {
          const hours = [];
          for (let i = currentHour + 1; i < 24; i++) {
            hours.push(i);
          }
          return hours;
        },
        disabledMinutes: (selectedHour: number) => {
          if (selectedHour === currentHour) {
            const minutes = [];
            for (let i = currentMinute + 1; i < 60; i++) {
              minutes.push(i);
            }
            return minutes;
          }
          return [];
        },
      };
    }

    return {};
  };

  useEffect(() => {
    if (isEdit) {
      const estabelecimentoId = form.getFieldValue(["estabelecimento", "id"]);
      getProgramacoesByEstabelecimento(estabelecimentoId);
      getMarcasProdutosByEstabelecimento(estabelecimentoId);
      getProdutosByEstabelecimento(estabelecimentoId);
      const natureza = form.getFieldValue("naturezaAnalise");

      getEnsaiosByNatureza(natureza);
    }
  }, []);

  return (
    <>
      <Row gutter={[8, 8]}>
        <Col xs={24} sm={12} md={8}>
          <Form.Item
            name={["estabelecimento", "id"]}
            label="Estabelecimento"
            rules={[{ required: true }]}
          >
            <Select
              placeholder="Selecione o Estabelecimento"
              showSearch
              optionFilterProp="label"
              options={estabelecimentoOptions}
              onChange={handleChangeEstabelecimento}
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Form.Item
            name="dataColeta"
            label="Data Coleta"
            rules={[{ required: true }]}
          >
            <DatePicker
              format={"DD/MM/YYYY HH:mm"}
              placeholder="Selecione a Data"
              showTime
              style={{ width: "100%" }}
              disabledDate={getDisabledDate}
              disabledTime={getDisabledTime}
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Form.Item name="etapaColeta" label="Etapa">
            <Select
              placeholder="Selecione a Etapa"
              options={[
                { label: "Fevereiro", value: "Fevereiro" },
                { label: "Abril", value: "Abril" },
                { label: "Junho", value: "Junho" },
                { label: "Agosto", value: "Agosto" },
                { label: "Outubro", value: "Outubro" },
                { label: "Dezembro", value: "Dezembro" },
              ]}
              allowClear
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Form.Item name="analiseFiscal" valuePropName="checked">
            <Checkbox>Análise Fiscal</Checkbox>
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Form.Item
            name="naturezaAnalise"
            label="Natureza Analise"
            rules={[{ required: true }]}
          >
            <Radio.Group onChange={handleChangeNaturezaAnalise}>
              <Row gutter={[8, 8]}>
                <Col xs={24} sm={24} md={24}>
                  <Radio value="MICROBIOLOGICA">MICROBIOLOGIA</Radio>
                </Col>
                <Col xs={24} sm={24} md={24}>
                  <Radio value="FISICOQUIMICO">FÍSICO QUÍMICA</Radio>
                </Col>
              </Row>
            </Radio.Group>
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Form.Item
            name="condicoesAmostra"
            label="Condições Amostra"
            rules={[{ required: true }]}
          >
            <Radio.Group>
              <Row gutter={[8, 8]}>
                <Col xs={24} sm={24} md={24} lg={12}>
                  <Radio value="CONGELADO">CONGELADO SÓLIDO</Radio>
                </Col>
                <Col xs={24} sm={24} md={24} lg={12}>
                  <Radio value="CRISTAIS">CRISTAIS DE GELO</Radio>
                </Col>
                <Col xs={24} sm={24} md={24} lg={12}>
                  <Radio value="RESFRIADO">RESFRIADO</Radio>
                </Col>
                <Col xs={24} sm={24} md={24} lg={12}>
                  <Radio value="AMBIENTE">AMBIENTE</Radio>
                </Col>
              </Row>
            </Radio.Group>
          </Form.Item>
        </Col>

        <Col xs={24} sm={24} md={8}>
          <Form.Item name={["programacao", "id"]} label="Programação">
            <Select
              placeholder="Selecione a Programação"
              filterSort={(optionA, optionB) =>
                (optionA?.label ?? "")
                  .toLowerCase()
                  .localeCompare((optionB?.label ?? "").toLowerCase())
              }
              virtual={false}
              optionFilterProp="label"
              showSearch
              options={programacoesOptions}
              onChange={handleChangeProgramacao}
              allowClear
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={16} md={12}>
          <Form.Item name={["produto", "id"]} label="Produto">
            <Select
              disabled={aguaChecked}
              placeholder="Selecione o Produto"
              onChange={handleChangeProduto}
              showSearch
              virtual={false}
              filterSort={(optionA, optionB) =>
                (optionA?.label ?? "")
                  .toLowerCase()
                  .localeCompare((optionB?.label ?? "").toLowerCase())
              }
              optionFilterProp="label"
              options={produtosOptions}
              allowClear
            />
          </Form.Item>
        </Col>

        <Col
          xs={24}
          sm={8}
          md={4}
          style={{ display: "flex", alignItems: "end" }}
        >
          <Form.Item name="agua" valuePropName="checked">
            <Checkbox onChange={handleChangeAgua}>Água</Checkbox>
          </Form.Item>
        </Col>

        {(isProgramacaoOutlier || isProgramacaoDiffProduto) && (
          <Col xs={24}>
            <Form.Item
              name="justificativaProgramacao"
              label="Justificativa"
              rules={[{ required: true }]}
            >
              <Input.TextArea placeholder="Informe a Justificativa para não cumprimento da Programação" />
            </Form.Item>
          </Col>
        )}

        <Col xs={24} sm={12} md={8}>
          <Form.Item
            name="informacoesAdicionais"
            label="Informações Adicionais"
          >
            <Input placeholder="Informe Informações Adicionais" />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Form.Item name={["marca", "id"]} label="Marca">
            <Select
              disabled={aguaChecked}
              placeholder="Selecione a Marca"
              optionFilterProp="label"
              showSearch
              filterSort={(optionA, optionB) =>
                (optionA?.label ?? "")
                  .toLowerCase()
                  .localeCompare((optionB?.label ?? "").toLowerCase())
              }
              options={marcasOptions}
              allowClear
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Form.Item
            name="dataFabricacao"
            label="Data Fabricação"
            rules={[{ required: !aguaChecked }]}
          >
            <DatePicker
              format={"DD/MM/YYYY"}
              placeholder="Selecione a Data"
              style={{ width: "100%" }}
              disabled={aguaChecked}
              allowClear
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Form.Item
            name="dataValidade"
            label="Data Validade"
            rules={[{ required: !aguaChecked }]}
          >
            <DatePicker
              format={"DD/MM/YYYY"}
              placeholder="Selecione a Data"
              style={{ width: "100%" }}
              disabled={aguaChecked}
              allowClear
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Form.Item name="lote" label="Lote">
            <Input placeholder="Informe o Lote" disabled={aguaChecked} />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Form.Item name="tamanhoPartida" label="Tamanho da Partida">
            <Input
              placeholder="Informe o Tamanho da Partida"
              disabled={aguaChecked}
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Form.Item name="lacre" label="Lacre">
            <Input placeholder="Informe o Lacre" />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Form.Item
            name="lacreContraProvaEmpresa"
            label="Lacre Contra Prova Empresa"
          >
            <Input placeholder="Informe o Lacre Contra Prova Empresa" />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Form.Item
            name="lacreContraProvaSISE"
            label="Lacre Contra Prova SISE"
          >
            <Input placeholder="Informe o Lacre Contra Prova SISE" />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Form.Item
            name="dataRemessa"
            label="Data Remessa"
            rules={[{ required: true }]}
          >
            <DatePicker
              format={"DD/MM/YYYY"}
              placeholder="Selecione a Data"
              style={{ width: "100%" }}
              allowClear
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Form.Item
            name="temperaturaAmostra"
            label="Temperatura da Amostra na Coleta"
          >
            <Input
              placeholder="Informe a Temperatura da Amostra"
              maxLength={9}
              onChange={(e) => {
                const { value } = e.target;
                form.setFieldValue(
                  "temperaturaAmostra",
                  temperaturaMask(value)
                );
              }}
              suffix="°C"
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Form.Item
            name={["laboratorio", "id"]}
            label="Laboratório"
            rules={[{ required: true }]}
          >
            <Select
              placeholder="Selecione o Laboratorio"
              showSearch
              filterSort={(optionA, optionB) =>
                (optionA?.label ?? "")
                  .toLowerCase()
                  .localeCompare((optionB?.label ?? "").toLowerCase())
              }
              optionFilterProp="label"
              options={laboratoriosOptions}
            />
          </Form.Item>
        </Col>

        <Col xs={24}>
          <Form.Item name="observacao" label="Observação">
            <Input.TextArea placeholder="Informe a Observação" />
          </Form.Item>
        </Col>

        <Divider orientation="left">
          <Typography.Title level={screens.xs ? 5 : 4}>
            Ensaios
          </Typography.Title>
        </Divider>

        <Col span={24}>
          <Form.Item>
            <Button
              type="primary"
              icon={<OrderedListOutlined />}
              onClick={handleOpenModalHistorico}
              title="Histórico de Ensaios"
              disabled={!isOnline || !estabelecimentoId || !naturezaAnalise}
            >
              {screens.xs ? "" : "Histórico Análise"}
            </Button>
          </Form.Item>
        </Col>

        <Col xs={24}>
          <Form.Item name="ensaios" rules={[{ required: true }]}>
            <Select
              placeholder="Selecione o Ensaios"
              mode="multiple"
              showSearch
              filterSort={(optionA, optionB) =>
                (optionA?.label ?? "")
                  .toLowerCase()
                  .localeCompare((optionB?.label ?? "").toLowerCase())
              }
              optionFilterProp="label"
              options={ensaioOptions}
            />
          </Form.Item>
        </Col>
      </Row>
      <ModalHistoricoAnalise
        open={isOpenModal}
        onClose={handleCloseModalHistorico}
        estabelecimentoId={estabelecimentoId}
        produtoId={produtoId}
        naturezaAnalise={naturezaAnalise}
      />
    </>
  );
}
